import { useIsInGoogleClassroom } from '@/shared/googleClassroom';
import { useIsInLti } from '@/shared/lti';

export type ExternalPlatformMode = 'none' | 'lti' | 'googleClassroom';

export interface ExternalPlatformState {
  mode: ExternalPlatformMode;
  isInExternalPlatform: boolean;
  isInLti: boolean;
  isInGoogleClassroom: boolean;
}

/**
 * Consolidated detection hook for external-platform modes (LTI / Google Classroom).
 * Use this when a component needs to branch on multiple modes. If you only need a single
 * mode, prefer the underlying useIsInLti() / useIsInGoogleClassroom() hooks.
 */
export function useExternalPlatform(): ExternalPlatformState {
  const isInLti = useIsInLti();
  const isInGoogleClassroom = useIsInGoogleClassroom();
  // LTI takes precedence if both somehow evaluate true (shouldn't happen in practice).
  let mode: ExternalPlatformMode = 'none';
  if (isInLti) mode = 'lti';
  else if (isInGoogleClassroom) mode = 'googleClassroom';
  if (process.env.NODE_ENV !== 'production' && isInLti && isInGoogleClassroom) {
    console.warn(
      '[useExternalPlatform] Both LTI and Google Classroom contexts detected; defaulting to LTI.',
    );
  }
  return {
    mode,
    isInExternalPlatform: mode !== 'none',
    isInLti,
    isInGoogleClassroom,
  };
}
